import { Link } from 'react-router-dom';
import JsonLd from '../components/JsonLd';
import { useDocumentMeta } from '../hooks/useDocumentMeta';
import { LIMES_STATE, STATE_DATE, fmtInt } from '../lib/siteState';

// Public product page for LIMES. Explains what the module is and for whom.
// Every functional surface (mapa, lista, ficha, CSV) lives behind login at
// /limes/:slug and is noindex; this page and /limes/como-funciona are the only
// indexable LIMES routes (docs/prod/SITE_PLAN.md section 2).
//
// Wording discipline (hard rule): priorização / situações a verificar, never
// "incumprimento detetado". The precision is stated, not hidden.

const SITE = 'https://tutela.land';

function Card({ title, body }: { title: string; body: string }) {
  return (
    <div className="bg-ink-900 p-8">
      <h3 className="font-display text-xl font-bold text-mist">{title}</h3>
      <p className="mt-3 text-mist-muted">{body}</p>
    </div>
  );
}

export default function Limes() {
  useDocumentMeta(
    'LIMES: priorização da gestão de faixas de combustível | Tutela',
    'O LIMES ordena, por freguesia e por parcela, as situações a verificar na rede secundária de faixas de gestão de combustível, a partir do LiDAR nacional, dos ortofotomapas e do cadastro. Para GTF e proteção civil municipal.',
    '/limes',
  );

  const breadcrumb = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Tutela', item: `${SITE}/` },
      { '@type': 'ListItem', position: 2, name: 'LIMES', item: `${SITE}/limes` },
    ],
  };

  const surfaces = [
    {
      title: 'Lista de prioridades',
      body: 'Uma lista ordenada das faixas e parcelas a verificar, filtrável por freguesia, com o motivo de cada linha. Exporta para CSV para o trabalho de campo e para os relatórios.',
    },
    {
      title: 'Ficha da parcela',
      body: 'A evidência por trás de cada linha: fração da faixa com vegetação acima de 2 m, estado estimado em 2025, série semanal de satélite, imagem da faixa e a regra que a colocou na lista.',
    },
    {
      title: 'Sinal de mudança',
      body: 'O que mudou entre o levantamento LiDAR de 2024 e os ortofotomapas de 2025, e o que muda semana a semana no Sentinel-2. Um sinal para orientar a visita, nunca um veredicto.',
    },
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-ink-950 text-mist">
      <JsonLd id="limes-breadcrumb" data={breadcrumb} />
      <div className="mx-auto max-w-3xl px-6 py-20">
        <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-ember/30 bg-ember/10 px-4 py-1.5 font-mono text-sm font-semibold text-ember">
          Módulo LIMES
        </div>
        <h1 className="font-display text-3xl font-bold tracking-tight text-mist sm:text-4xl">
          Priorização da gestão de faixas de combustível, parcela a parcela
        </h1>
        <p className="mt-6 text-lg leading-relaxed text-mist-muted">
          O LIMES lê o território de um concelho a partir do levantamento LiDAR nacional, dos
          ortofotomapas de 2025 e do cadastro, e ordena, por freguesia e por parcela, as situações
          a verificar na rede secundária de faixas de gestão de combustível. Foi feito para o
          Gabinete Técnico Florestal e para o Serviço Municipal de Proteção Civil.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          <Link
            to="/limes/como-funciona"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Como funciona
          </Link>
          <Link
            to="/municipios"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-6 py-3 font-medium text-mist transition-colors hover:bg-ink-800"
          >
            Para municípios
          </Link>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            O problema
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Centenas de hectares de faixas, uma equipa, uma janela curta
          </h2>
          <div className="mt-6 space-y-4 leading-relaxed text-mist-muted">
            <p>
              Os programas municipais de execução obrigam a monitorizar a rede secundária de faixas de
              gestão de combustível e a registar, todos os anos, as ações executadas. No terreno isso
              significa centenas de parcelas, prazos legais de 30 e 60 dias e uma janela de execução
              que fecha antes do período crítico. Ninguém consegue cobrir tudo a pé.
            </p>
            <p>
              O LIMES responde a uma pergunta operacional: <span className="text-mist">onde devemos
              ir esta semana?</span> Não responde a "quem está em incumprimento", porque essa resposta
              exige uma verificação no local que só o município pode fazer.
            </p>
          </div>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            O que aparece no ecrã
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Três superfícies, uma só história
          </h2>
          <div className="mt-8 grid gap-px overflow-hidden rounded-xl border border-ink-700 bg-ink-700 md:grid-cols-3">
            {surfaces.map((s) => (
              <Card key={s.title} title={s.title} body={s.body} />
            ))}
          </div>
          <p className="mt-6 text-sm leading-relaxed text-mist-faint">
            O mapa de cada concelho pinta apenas as faixas no topo da lista de prioridades; tudo o resto
            fica em contorno, sobre o ortofotomapa. Quando 70% do território grita, nada grita.
          </p>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            O que não é
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Priorização, não deteção de incumprimento
          </h2>
          <div className="mt-6 space-y-4 leading-relaxed text-mist-muted">
            <p>
              A sinalização de uma parcela é uma <span className="text-mist">situação a verificar</span>,
              não uma prova de infração. Nas nossas medições a precisão da sinalização situa-se entre
              54% e 74%: útil para ordenar uma semana de trabalho, insuficiente para um auto. A ficha
              de evidência apoia a inspeção; não a substitui.
            </p>
            <p>
              "Sem intervenção detetada" também não quer dizer "em incumprimento": a parcela pode
              estar naturalmente limpa, ser pastagem, estar sob outro regime ou ter sido limpa abaixo
              do que a imagem consegue distinguir. A pontuação é visivelmente uma pontuação, não um
              sim ou não.
            </p>
            <p>
              A classificação oficial de perigosidade e os prazos legais continuam a ser os do ICNF e
              do decreto-lei 82/2021. O LIMES ajuda a cumprir esses deveres; não os redefine.
            </p>
          </div>
        </div>

        <div className="mt-16 border-t border-ink-800 pt-12">
          <div className="mb-2 font-mono text-xs uppercase tracking-[0.22em] text-ember">
            Onde está
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight text-mist sm:text-3xl">
            Em avaliação com Vila Franca de Xira, em produção desde setembro de 2026
          </h2>
          <div className="mt-6 space-y-4 leading-relaxed text-mist-muted">
            <p>
              O primeiro piloto corre com a Câmara Municipal de Vila Franca de Xira, no âmbito de um
              projeto de investigação do CIAITeX, sobre toda a rede secundária do concelho:{' '}
              {fmtInt(LIMES_STATE.unitsEvaluated)} das {fmtInt(LIMES_STATE.unitsTotal)} unidades avaliadas a
              partir de {LIMES_STATE.orthoTiles} folhas de ortofotomapa de 2025.
            </p>
            <p>
              Estado em {STATE_DATE}: a consola, a lista de prioridades, a ficha e a exportação estão em
              produção em tutela.land desde {LIMES_STATE.liveSince}, com {LIMES_STATE.namedAccounts} contas
              nomeadas do município. A lista tem {fmtInt(LIMES_STATE.verificar)} situações a verificar e{' '}
              {fmtInt(LIMES_STATE.reducao)} unidades com redução clara de vegetação entre o LiDAR de 2024 e
              os ortofotomapas de 2025; a série semanal de Sentinel-2 leva {LIMES_STATE.s2Weeks} semanas. O
              relatório de estado da rede, as fichas por situação e o registo cartográfico anual são gerados
              a partir destes dados. A verificação no terreno pelo município, que é o que mede a precisão
              real da lista, ainda não foi feita.
            </p>
            <p>
              Os modelos foram treinados e medidos nesse território: um município, um modelo afinado
              nas suas faixas. Para outro concelho o primeiro passo é uma prova sobre as faixas desse
              concelho, antes de qualquer número. As funcionalidades estão disponíveis após início de
              sessão, com contas por entidade e cada município a ver apenas o seu território. Ver o estado dos pilotos em{' '}
              <Link to="/pilotos" className="font-medium text-ember hover:brightness-110">
                Pilotos
              </Link>
              .
            </p>
          </div>
        </div>

        <div className="mt-16 flex flex-wrap gap-4 border-t border-ink-800 pt-8">
          <a
            href="mailto:hello@tutela.land?subject=LIMES"
            className="inline-flex items-center gap-2 rounded-lg bg-ember px-6 py-3 font-semibold text-ink-950 shadow-[0_0_30px_-8px_#F26A4B] transition-all duration-200 hover:brightness-110"
          >
            Falar connosco
          </a>
          <Link
            to="/limes/como-funciona"
            className="inline-flex items-center gap-2 rounded-lg border border-ink-700 px-6 py-3 font-medium text-mist transition-colors hover:bg-ink-800"
          >
            Como funciona
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-2 rounded-lg px-5 py-3 font-mono text-sm uppercase tracking-widest text-mist-muted transition-colors hover:text-mist"
          >
            Voltar a Tutela
          </Link>
        </div>
      </div>
    </div>
  );
}
